import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Label, Input, Table, Button, Spinner } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import Header from "../../../layout/Header/Header";

/**
 * Ajuste de inventario: pasar piezas a Saldos y regresarlas.
 *
 * Saldos es un almacen aparte; lo que esta ahi se puede seguir surtiendo, pero
 * ya no cuenta en el Disponible de la venta normal.
 */
function AjusteInventarios() {
  const { user } = useContext(AuthContext);
  const URL_INVENTARIOS = process.env.REACT_APP_URL_INVENTARIOS;
  const URL_AREAS = process.env.REACT_APP_URL_AREAS;
  const URL_AJUSTES = process.env.REACT_APP_URL_AJUSTES_INVENTARIO;

  const [pestana, setPestana] = useState("inventario");
  const [renglones, setRenglones] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [ajustes, setAjustes] = useState([]);
  const [cantidades, setCantidades] = useState({});
  const [motivo, setMotivo] = useState("");
  const [bodegaRegreso, setBodegaRegreso] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [cargando, setCargando] = useState(false);
  const [recarga, setRecarga] = useState(0);

  const cab = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });

  useEffect(() => {
    setCargando(true);
    Promise.all([
      axios.get(`${URL_INVENTARIOS}Desglosado`, cab()),
      axios.get(URL_AREAS, cab()),
      axios.get(URL_AJUSTES, cab()),
    ])
      .then(([inv, areas, aj]) => {
        setRenglones(
          inv.data.map((a) => ({
            _id: a._id,
            tipoArea: a.areas?.[0]?.tipo || "Bodega",
            area: a.areas?.[0]?.name ?? "",
            codigo: [a.articulos?.[0]?.codigo, a.articulos?.[0]?.codigoCliente].filter(Boolean).join(" / "),
            articulo: a.articulos?.[0]?.descripcion ?? "",
            color: a.colores?.[0]?.name ?? "",
            talla: a.tallas?.[0]?.name ?? "",
            cantidad: a.cantidad || 0,
          }))
        );
        const bs = areas.data.filter((a) => (a.tipo || "Bodega") === "Bodega");
        setBodegas(bs);
        if (bs.length && !bodegaRegreso) setBodegaRegreso(bs[0]._id);
        setAjustes(aj.data);
      })
      .catch((err) => console.log(err))
      .finally(() => setCargando(false));
  }, [recarga]);

  const enSaldos = pestana === "saldos";
  const texto = busqueda.toLowerCase();
  const visibles = renglones
    .filter((r) => (enSaldos ? r.tipoArea === "Saldos" : r.tipoArea === "Bodega"))
    .filter((r) => r.cantidad > 0)
    .filter((r) => !texto || `${r.codigo} ${r.articulo} ${r.color} ${r.talla} ${r.area}`.toLowerCase().includes(texto))
    .sort((a, b) => (a.codigo + a.color + a.talla > b.codigo + b.color + b.talla ? 1 : -1));

  const elegidos = visibles.filter((r) => (parseFloat(cantidades[r._id]) || 0) > 0);
  const totalElegido = elegidos.reduce((t, r) => t + parseFloat(cantidades[r._id]), 0);
  const totalSaldos = renglones.filter((r) => r.tipoArea === "Saldos").reduce((t, r) => t + r.cantidad, 0);

  const cambiaCantidad = (id, valor, maximo) => {
    const n = parseFloat(valor);
    setCantidades({ ...cantidades, [id]: isNaN(n) || n < 0 ? "" : Math.min(n, maximo) });
  };

  const cambiarPestana = (p) => {
    setPestana(p);
    setCantidades({});
  };

  async function aplicar() {
    if (!elegidos.length) return Swal.fire({ icon: "warning", title: "Captura cuantas piezas mover" });
    const renglonesEnvio = elegidos.map((r) => ({ inventarios: r._id, cantidad: parseFloat(cantidades[r._id]) }));
    const r = await Swal.fire({
      icon: "question",
      title: enSaldos ? `Regresar ${totalElegido} pzas a bodega?` : `Pasar ${totalElegido} pzas a Saldos?`,
      showCancelButton: true,
      confirmButtonText: "Si",
      cancelButtonText: "No",
    });
    if (!r.value) return;
    try {
      const fecha = moment().format("YYYY-MM-DD");
      const { data } = enSaldos
        ? await axios.post(`${URL_AJUSTES}/regresar`, { fecha, areaDestino: bodegaRegreso, motivo, renglones: renglonesEnvio }, cab())
        : await axios.post(`${URL_AJUSTES}/saldos`, { fecha, motivo, renglones: renglonesEnvio }, cab());
      Swal.fire({ icon: "success", title: `Ajuste ${data.idAjusteInventario} aplicado`, text: `${data.total} pzas` });
      setCantidades({});
      setMotivo("");
      setRecarga(recarga + 1);
    } catch (error) {
      const info = error.response?.data || {};
      const detalle = (info.faltantes || [])
        .map((f) => `${f.material} ${f.color || ""} ${f.talla || ""}: hay ${f.existencia}, pediste ${f.requerido}`)
        .join("<br/>");
      Swal.fire({ icon: "error", title: "No se pudo aplicar", html: detalle || info.message || error.message });
    }
  }

  const Pestana = ({ id, children }) => (
    <Button
      size="sm"
      color={pestana === id ? "primary" : "secondary"}
      outline={pestana !== id}
      onClick={() => cambiarPestana(id)}
      style={{ marginRight: 6 }}
    >
      {children}
    </Button>
  );

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_inventarios ? (
        <div className="card container col-12">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <i className="fas fa-sliders-h" style={{ marginRight: 8, color: "#0ea5e9" }} />
                Ajuste de Inventario
              </h3>
              <p className="page-subtitle">Pasa piezas a Saldos o regresalas a bodega</p>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <Pestana id="inventario">Inventario</Pestana>
            <Pestana id="saldos">Saldos ({new Intl.NumberFormat("en-US").format(totalSaldos)} pzas)</Pestana>
            <Pestana id="movimientos">Movimientos</Pestana>
          </div>

          {cargando ? (
            <Spinner />
          ) : pestana === "movimientos" ? (
            <Table size="sm" striped borderless responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>De</th>
                  <th>A</th>
                  <th>Piezas</th>
                  <th>Motivo</th>
                  <th>Usuario</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {ajustes.map((a) => (
                  <tr key={a._id}>
                    <td>{a.idAjusteInventario}</td>
                    <td>{a.fecha}</td>
                    <td>{a.tipo}</td>
                    <td>{a.areaOrigen?.name}</td>
                    <td>{a.areaDestino?.name}</td>
                    <td>{a.total}</td>
                    <td>{a.motivo}</td>
                    <td>{[a.user?.nombre, a.user?.apellido].filter(Boolean).join(" ")}</td>
                    <td style={{ fontSize: 12 }}>
                      {(a.renglones || [])
                        .map((r) => `${r.articulos?.codigo ?? ""} ${r.colores?.name ?? ""} ${r.tallas?.name ?? ""}: ${r.cantidad}`)
                        .join(" · ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <>
              <Row style={{ marginBottom: 12 }}>
                <Col md={4}>
                  <Label>Buscar</Label>
                  <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Codigo, articulo, color, talla o almacen" />
                </Col>
                <Col md={4}>
                  <Label>Motivo</Label>
                  <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder={enSaldos ? "Por que regresa" : "Ej. fin de temporada"} />
                </Col>
                {enSaldos ? (
                  <Col md={2}>
                    <Label>Regresar a</Label>
                    <Input type="select" value={bodegaRegreso} onChange={(e) => setBodegaRegreso(e.target.value)}>
                      {bodegas.map((b) => (
                        <option value={b._id} key={b._id}>{b.name}</option>
                      ))}
                    </Input>
                  </Col>
                ) : undefined}
                <Col md={2} style={{ display: "flex", alignItems: "flex-end" }}>
                  <Button color={enSaldos ? "info" : "warning"} onClick={aplicar} disabled={!elegidos.length}>
                    {enSaldos ? "Regresar" : "Pasar a saldos"} {totalElegido ? `(${totalElegido})` : ""}
                  </Button>
                </Col>
              </Row>

              <Table size="sm" striped borderless responsive>
                <thead>
                  <tr>
                    <th>Almacen</th>
                    <th>Codigo</th>
                    <th>Articulo</th>
                    <th>Color</th>
                    <th>Talla</th>
                    <th style={{ textAlign: "right" }}>Existencia</th>
                    <th style={{ width: 130 }}>{enSaldos ? "Regresar" : "A saldos"}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((r) => (
                    <tr key={r._id}>
                      <td>{r.area}</td>
                      <td>{r.codigo}</td>
                      <td>{r.articulo}</td>
                      <td>{r.color}</td>
                      <td>{r.talla}</td>
                      <td style={{ textAlign: "right" }}>{r.cantidad}</td>
                      <td>
                        <Input
                          bsSize="sm"
                          type="number"
                          min={0}
                          max={r.cantidad}
                          value={cantidades[r._id] ?? ""}
                          onChange={(e) => cambiaCantidad(r._id, e.target.value, r.cantidad)}
                        />
                      </td>
                    </tr>
                  ))}
                  {!visibles.length ? (
                    <tr>
                      <td colSpan={7} className="text-muted">
                        {enSaldos ? "No hay piezas en Saldos." : "No hay existencias en bodega."}
                      </td>
                    </tr>
                  ) : undefined}
                </tbody>
              </Table>
            </>
          )}
        </div>
      ) : undefined}
    </>
  );
}

export default AjusteInventarios;
